import React from 'react';
import { Card, C } from './UI';

interface AccuracyData {
    jobId: string;
    address: string;
    estimatedUnits: number;
    actualUnits: number;
    accuracy: number;
    hasPhoto: boolean;
    createdAt: string;
}

export function AIAccuracyWidget({ jobs }: { jobs: any[] }) {
    const jobsWithBoth = jobs.filter(j =>
        j.actualUnits != null &&
        j.estimatedUnits != null &&
        j.status === 'Completed'
    ) as AccuracyData[];

    if (jobsWithBoth.length === 0) {
        return (
            <Card>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                    AI Estimation Accuracy
                </div>
                <div style={{ textAlign: 'center', padding: '24px 0', color: C.textMuted, fontSize: 13 }}>
                    No completed jobs with actual unit counts yet.
                    Accuracy data will appear here as contractors complete jobs.
                </div>
            </Card>
        );
    }

    // Calculate accuracy metrics
    const accuracies = jobsWithBoth.map(j => ({
        ...j,
        accuracy: Math.round((1 - Math.abs(j.actualUnits - j.estimatedUnits) / j.actualUnits) * 100)
    }));

    const avgAccuracy = Math.round(accuracies.reduce((s, j) => s + j.accuracy, 0) / accuracies.length);
    const within10 = accuracies.filter(j => j.accuracy >= 90).length;
    const within25 = accuracies.filter(j => j.accuracy >= 75).length;
    const overEstimated = accuracies.filter(j => j.estimatedUnits > j.actualUnits).length;
    const underEstimated = accuracies.filter(j => j.estimatedUnits < j.actualUnits).length;

    const accuracyColor = (acc: number) => {
        if (acc >= 90) return C.greenMid;
        if (acc >= 75) return '#EF9F27';
        return '#C0392B';
    };

    return (
        <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
                AI Estimation Accuracy
            </div>

            {/* Summary stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={{ textAlign: 'center', padding: 12, background: C.greenBg, borderRadius: 8 }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: accuracyColor(avgAccuracy) }}>
                        {avgAccuracy}%
                    </div>
                    <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>Avg accuracy</div>
                </div>
                <div style={{ textAlign: 'center', padding: 12, background: C.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: C.greenMid }}>
                        {within10}
                    </div>
                    <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>Within 10%</div>
                </div>
                <div style={{ textAlign: 'center', padding: 12, background: C.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#EF9F27' }}>
                        {overEstimated}
                    </div>
                    <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>Over-estimated</div>
                </div>
                <div style={{ textAlign: 'center', padding: 12, background: C.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#C0392B' }}>
                        {underEstimated}
                    </div>
                    <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>Under-estimated</div>
                </div>
            </div>

            {/* Per-job breakdown */}
            <div style={{ fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 8 }}>
                Recent jobs ({accuracies.length} with actual counts)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {accuracies.slice(-20).reverse().map(j => (
                    <div key={j.jobId} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', background: C.bg, borderRadius: 8,
                        borderLeft: `3px solid ${accuracyColor(j.accuracy)}`
                    }}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>
                                {j.address}
                            </div>
                            <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
                                AI: {j.estimatedUnits} · Actual: {j.actualUnits}
                                {' '}·{' '}
                                <span style={{ color: j.estimatedUnits > j.actualUnits ? '#EF9F27' : '#C0392B' }}>
                                    {j.estimatedUnits > j.actualUnits ? '▲ over' : '▼ under'} by {Math.abs(j.estimatedUnits - j.actualUnits)}
                                </span>
                            </div>
                        </div>
                        <div style={{
                            fontSize: 16, fontWeight: 700,
                            color: accuracyColor(j.accuracy),
                            minWidth: 48, textAlign: 'right'
                        }}>
                            {j.accuracy}%
                        </div>
                    </div>
                ))}
            </div>

            {/* Training data note */}
            <div style={{
                marginTop: 16, padding: 12,
                background: '#FAEEDA', borderRadius: 8,
                fontSize: 12, color: '#854F0B'
            }}>
                💡 <strong>{jobsWithBoth.length} labeled examples</strong> available for AI training.
                {jobsWithBoth.length < 50
                    ? ` Collect ${50 - jobsWithBoth.length} more to enable few-shot prompt improvement.`
                    : jobsWithBoth.length < 100
                        ? ` Collect ${100 - jobsWithBoth.length} more to enable fine-tuning.`
                        : ' You have enough data to fine-tune a custom model.'
                }
            </div>
        </Card>
    );
}
